// Sponte pode retornar escalares XML como arrays. Todas as contagens usam a mesma base.
export const textValue = value => Array.isArray(value) ? textValue(value[0]) : String(value ?? '').trim();
const field = (row, keys) => keys.map(key => textValue(row?.[key])).find(Boolean) || '';
const canonical = value => textValue(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/[º°ª]/g, '').replace(/\s+/g, ' ').trim();
export const classroomName = row => field(row, ['NomeTurma', 'TurmaNome', 'Turma']) || 'Turma não informada';
export const studentId = row => field(row, ['AlunoID', 'AlunoId', 'AlunoIDMatricula', 'AlunoIdSponte']);
export const classroomKey = name => canonical(name).replace(/\s*\b20\d{2}\b\s*$/, '').trim(); // Preservar turno e letra.
export const gradeName = name => classroomKey(name).replace(/\s*\([^)]+\)/g, '').replace(/\s*-\s*[A-Z]\s*$/, '').trim().replace(/\b(\d+) ANO\b/g, '$1º ANO').replace(/\b(\d+) SERIE\b/g, '$1ª SÉRIE');
export const shiftName = name => {
  const key = canonical(name);
  return key.includes('MANHA') ? 'MANHÃ' : key.includes('TARDE') ? 'TARDE' : key.includes('NOITE') ? 'NOITE' : 'Não informado';
};
export const levelName = name => {
  const key = canonical(name);
  if (/INFANTIL|MATERNAL|BERCARIO/.test(key)) return 'Infantil';
  if (/SERIE|\/EM\b|ENSINO MEDIO/.test(key)) return 'Ensino Médio';
  const year = /\b([1-9])\s*ANO\b/.exec(key);
  return year ? Number(year[1]) <= 5 ? 'Fundamental I' : 'Fundamental II' : 'Outros / não informado';
};
export const gradeSort = (a, b) => {
  const order = ['Infantil', 'Fundamental I', 'Fundamental II', 'Ensino Médio', 'Outros / não informado'];
  return order.indexOf(levelName(a)) - order.indexOf(levelName(b)) || a.localeCompare(b, 'pt-BR', {
    numeric: true
  });
};
export function readEnrollmentResponse(payload) {
  const rows = Array.isArray(payload) ? payload : payload?.matriculas;
  if (!Array.isArray(rows)) throw new Error('Resposta de matrículas inválida');
  for (const row of rows) {
    const code = textValue(row.RetornoOperacao);
    if (code && !/^(?:0?1|43)(?:\s*-|\s*$)/.test(code)) throw new Error('Consulta Sponte não confirmada');
  }
  return rows;
}
export function normalizeEnrollments(rows, year) {
  const seen = new Set();
  return rows.filter(row => {
    const contract = field(row, ['ContratoID', 'ContratoId', 'ContratoAulaLivreID']);
    if (!/^[1-9]\d*$/.test(contract) || !/^[1-9]\d*$/.test(studentId(row)) || /^43(?:\s*-|$)/.test(textValue(row.RetornoOperacao))) return false; // Não contar a linha de retorno 43 como um aluno.
    const name = classroomName(row);
    if (/INTEGRAL/.test(canonical(name))) return false;
    const explicitYear = field(row, ['AnoLetivo']) || /\b(20\d{2})\s*$/.exec(name)?.[1];
    if (explicitYear && Number(explicitYear) !== Number(year)) return false;
    const key = `${contract}:${studentId(row)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
// Progressão esperada é uma referência curricular, não uma afirmação de aprovação.
export function enrollmentStage(name) {
  const key = canonical(name);
  const roman = ['I', 'II', 'III', 'IV', 'V'];
  const infant = /\bINFANTIL\s*(IV|V|III|II|I|[1-5])\b/.exec(key);
  if (infant) {
    const number = /^\d$/.test(infant[1]) ? Number(infant[1]) : roman.indexOf(infant[1]) + 1;
    return {
      code: `EI${number}`,
      name: `INFANTIL ${roman[number - 1]}`,
      next: number < 5 ? `EI${number + 1}` : 'EF1',
      nextName: number < 5 ? `INFANTIL ${roman[number]}` : '1º ANO/EF'
    };
  }
  const medium = /\b([1-3])\s*(?:SERIE|ANO)\b/.exec(key);
  if (medium && /SERIE|\/EM\b|ENSINO MEDIO/.test(key)) {
    const number = Number(medium[1]);
    return {
      code: `EM${number}`,
      name: `${number}ª SÉRIE/EM`,
      next: number < 3 ? `EM${number + 1}` : null,
      nextName: number < 3 ? `${number + 1}ª SÉRIE/EM` : 'Conclusão do Ensino Médio',
      terminal: number === 3
    };
  }
  const fundamental = /\b([1-9])\s*ANO\b/.exec(key);
  if (fundamental) {
    const number = Number(fundamental[1]);
    return {
      code: `EF${number}`,
      name: `${number}º ANO/EF`,
      next: number < 9 ? `EF${number + 1}` : 'EM1',
      nextName: number < 9 ? `${number + 1}º ANO/EF` : '1ª SÉRIE/EM'
    };
  }
  return null;
}
export function enrichEnrollmentClassrooms(rows, year, catalog) {
  if (!Array.isArray(catalog)) return rows;
  const map = new Map(catalog.map(row => [textValue(row.TurmaID), row]));
  return rows.map(row => {
    const official = map.get(textValue(row.TurmaID));
    const name = textValue(official?.Nome);
    if (!name) return row;
    return {
      ...row,
      NomeTurma: name,
      AnoLetivo: textValue(official.AnoLetivo) || String(year),
      _officialShift: shiftName(textValue(official.Turno))
    };
  });
}
const enrollmentShift = row => row._officialShift && row._officialShift !== 'Não informado' ? row._officialShift : shiftName(classroomName(row));
export function buildEnrollmentCohorts(rows, before) {
  const current = new Map(),
    origins = new Map();
  rows.forEach(row => {
    const id = studentId(row);
    if (!current.has(id)) current.set(id, []);
    current.get(id).push(row);
  });
  before.forEach(row => {
    const id = studentId(row);
    if (!origins.has(id)) origins.set(id, []);
    origins.get(id).push(row);
  });
  const cohorts = new Map();
  origins.forEach((source, id) => {
    const sourceGrades = new Set(source.map(row => enrollmentStage(classroomName(row))?.name || gradeName(classroomName(row))));
    const conflict = sourceGrades.size > 1;
    const name = conflict ? 'Origem em mais de uma série' : [...sourceGrades][0];
    const stage = conflict ? null : enrollmentStage(classroomName(source[0]));
    if (!cohorts.has(name)) cohorts.set(name, {
      name,
      expectedName: stage?.nextName || 'Progressão não mapeada',
      terminal: Boolean(stage?.terminal),
      total: 0,
      located: 0,
      expected: 0,
      sameGrade: 0,
      otherGrade: 0,
      review: 0,
      turnChanges: 0,
      missing: 0,
      details: []
    });
    const cohort = cohorts.get(name);
    const destinations = current.get(id) || [];
    const destinationCodes = new Set(destinations.map(row => enrollmentStage(classroomName(row))?.code || 'unknown'));
    let status = 'missing';
    if (destinations.length) {
      cohort.located++;
      if (conflict || !stage || destinationCodes.has('unknown') || destinationCodes.size !== 1) status = 'review';else if (destinationCodes.has(stage.next)) status = 'expected';else if (destinationCodes.has(stage.code)) status = 'sameGrade';else status = 'otherGrade';
    }
    const priorTurns = new Set(source.map(enrollmentShift)),
      nextTurns = new Set(destinations.map(enrollmentShift));
    const changedShift = priorTurns.size === 1 && nextTurns.size === 1 && !priorTurns.has('Não informado') && !nextTurns.has('Não informado') && [...priorTurns][0] !== [...nextTurns][0];
    cohort.total++;
    cohort[status]++;
    if (changedShift) cohort.turnChanges++;
    const personName = [...source, ...destinations].map(row => field(row, ['Aluno', 'AlunoNome', 'NomeAluno'])).find(Boolean) || 'Nome não informado';
    cohort.details.push({
      id,
      name: personName,
      status,
      changedShift,
      originClasses: [...new Set(source.map(classroomName))],
      destinationClasses: [...new Set(destinations.map(classroomName))],
      originTurns: [...priorTurns],
      destinationTurns: [...nextTurns]
    });
  });
  return [...cohorts.values()].sort((a, b) => gradeSort(a.name, b.name));
}
export function auditEnrollmentStatistics(stats, rows, before, ended) {
  const sum = (list, key) => list.reduce((value, row) => value + row[key], 0);
  const checks = {
    types: stats.new + stats.returning + stats.unknown === stats.total,
    classrooms: sum(stats.classrooms, 'total') === stats.total,
    grades: sum(stats.grades, 'total') === stats.total,
    segments: sum(stats.segments, 'value') === stats.total,
    shifts: sum(stats.shifts, 'value') === stats.total,
    historical: before === null || sum(stats.classrooms, 'previous') === before.length,
    rescinded: ended === null || sum(stats.classrooms, 'rescinded') === ended.length,
    cohorts: before === null || sum(stats.cohorts, 'total') === new Set(before.map(studentId)).size,
    destinations: stats.cohorts.every(row => row.located + row.missing === row.total && row.expected + row.sameGrade + row.otherGrade + row.review === row.located && row.details.length === row.total)
  };
  const previousIds = new Set((before || []).map(studentId));
  const contradictoryNew = new Set(rows.filter(row => previousIds.has(studentId(row)) && Number(textValue(row.TipoContratoID)) === -1).map(studentId)).size;
  const multipleGrades = list => {
    const students = new Map();
    list.forEach(row => {
      const id = studentId(row);
      if (!students.has(id)) students.set(id, new Set());
      students.get(id).add(enrollmentStage(classroomName(row))?.name || gradeName(classroomName(row)));
    });
    return [...students.values()].filter(grades => grades.size > 1).length;
  };
  return {
    valid: Object.values(checks).every(Boolean),
    checks,
    contradictoryNew,
    multipleCurrentGrades: multipleGrades(rows),
    multiplePreviousGrades: multipleGrades(before || []),
    multipleContracts: rows.length - stats.students
  };
}
export function buildEnrollmentStatistics(current, previous, rescinded, year, catalogs = {}) {
  const rows = normalizeEnrollments(enrichEnrollmentClassrooms(current, year, catalogs.current), year);
  const before = previous === null ? null : normalizeEnrollments(enrichEnrollmentClassrooms(previous, year - 1, catalogs.previous), year - 1);
  const ended = rescinded === null ? null : normalizeEnrollments(enrichEnrollmentClassrooms(rescinded, year, catalogs.current), year);
  const group = list => {
    const result = {};
    list.forEach(row => {
      const name = classroomName(row),
        key = `${classroomKey(name)} | ${enrollmentShift(row)}`;
      const item = result[key] ||= {
        name,
        key,
        grade: gradeName(name),
        level: levelName(name),
        shift: enrollmentShift(row),
        total: 0,
        new: 0,
        returning: 0,
        unknown: 0
      };
      item.total++;
      const type = Number(textValue(row.TipoContratoID));
      item[type === -1 ? 'new' : type === -2 ? 'returning' : 'unknown']++;
    });
    return result;
  };
  const classes = group(rows),
    oldClasses = before === null ? null : group(before),
    endedClasses = ended === null ? null : group(ended);
  // Incluir turmas que só existem no histórico ou nos rescindidos, sem ocultar registros.
  const keys = [...new Set([...Object.keys(classes), ...Object.keys(oldClasses || {}), ...Object.keys(endedClasses || {})])];
  const classrooms = keys.map(key => ({
    ...(classes[key] || {
      ...(oldClasses?.[key] || endedClasses[key]),
      total: 0,
      new: 0,
      returning: 0,
      unknown: 0
    }),
    previous: before === null ? null : oldClasses[key]?.total || 0,
    rescinded: ended === null ? null : endedClasses[key]?.total || 0
  }));
  const grades = {};
  classrooms.forEach(row => {
    const item = grades[row.grade] ||= {
      name: row.grade,
      level: row.level,
      total: 0,
      new: 0,
      returning: 0,
      unknown: 0,
      previous: before === null ? null : 0,
      rescinded: ended === null ? null : 0
    };
    for (const key of ['total', 'new', 'returning', 'unknown', 'previous', 'rescinded']) if (row[key] !== null) item[key] += row[key];
  });
  const aggregate = getKey => Object.values(rows.reduce((map, row) => {
    const name = getKey(row);
    (map[name] ||= {
      name,
      value: 0
    }).value++;
    return map;
  }, {}));
  const totals = Object.values(classes).reduce((a, row) => ({
    total: a.total + row.total,
    new: a.new + row.new,
    returning: a.returning + row.returning,
    unknown: a.unknown + row.unknown
  }), {
    total: 0,
    new: 0,
    returning: 0,
    unknown: 0
  });
  const enrolledIds = new Set(rows.map(studentId));
  const cohorts = before === null ? [] : buildEnrollmentCohorts(rows, before);
  const result = {
    ...totals,
    students: enrolledIds.size,
    rescinded: ended === null ? null : ended.length,
    classrooms,
    grades: Object.values(grades).sort((a, b) => gradeSort(a.name, b.name)),
    segments: aggregate(row => levelName(classroomName(row))),
    shifts: aggregate(enrollmentShift),
    types: [{
      name: 'Novatos',
      value: totals.new
    }, {
      name: 'Veteranos',
      value: totals.returning
    }, {
      name: 'Tipo não informado',
      value: totals.unknown
    }].filter(row => row.value),
    cohorts
  };
  result.audit = auditEnrollmentStatistics(result, rows, before, ended);
  return result;
}
